import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router'
import { Button } from '#/components/ui/button'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '#/components/ui/form'
import { Input } from '#/components/ui/input'
import { AlertBox } from '#/components/ui/alert-box'
import { mutationError } from '#/lib/mutation-error'
import { organizationAPI } from '#/api/organization'
import { stickerAPI } from '#/api/sticker'


interface FormInputs {
  id: string
  connectorId: number
}

export default function NewStickerModal() {
  const { t } = useTranslation([ 'stickers', 'general' ])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const params = useParams() as { organization: string }

  const close = () => navigate('..')

  // Fetch the organization for longId
  const { data: organization } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization ],
    queryFn: () => organizationAPI.get(params.organization)
  })

  const form = useForm<FormInputs>({
    defaultValues: {
      id: '',
      connectorId: 0
    }
  })

  // Mutation to create sticker
  const mutation = useMutation({
    mutationFn: (data: FormInputs) => stickerAPI.create({
      organizationId: organization.id,
      ...data,
      connectorId: Number.parseInt(data.connectorId.toString()) // TODO: check this type error
    }),
    onSuccess: created => {
      queryClient.setQueryData(
        [ 'organizations', params.organization, 'stickers', created.id ],
        created
      )
      navigate('..')
    },
    onError: () => {
      // handled via AlertBox
    }
  })

  return (
    <Dialog open modal onOpenChange={close}>
      <DialogContent className='max-h-[80%] overflow-y-scroll'>
        <DialogHeader>
          <DialogTitle>{t('new_sticker.title')}</DialogTitle>
          <DialogDescription>{t('new_sticker.description')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(values => mutation.mutate(values))}
            className="flex flex-col gap-4"
            autoComplete="off"
          >

            {mutation.error && (
              <AlertBox
                error
                title={mutationError(mutation.error, {
                  default: t('new_sticker.errors.default'),
                  forbidden: t('new_sticker.errors.forbidden')
                })}
              />
            )}

            {/* Id */}
            <FormField
              name="id"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.id')}</FormLabel>
                  <FormControl>
                    <Input
                      required
                      maxLength={20}
                      autoComplete="off"
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Connector ID */}
            <FormField
              name="connectorId"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.connectorId')}</FormLabel>
                  <FormControl>
                    <Input
                      type='number'
                      required
                      maxLength={20}
                      autoComplete="off"
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 justify-start">
              <Button type="button" variant="outline" onClick={close}>
                {t('general:cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {t('new_sticker.submit')}
              </Button>
            </DialogFooter>

          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
